import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { 
    Shield, LogOut, Activity, UserPlus, Building, Users, User, Hexagon, FileText, BarChart2, Bell, Settings, Pill, BookText, LayoutDashboard,
    UserCog, UserCheck, FlaskConical
} from 'lucide-react';

const Layout = ({ children }) => {
    const { user, logout } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [onlineUsers, setOnlineUsers] = useState(1);

    useEffect(() => {
        const trackOnline = async () => {
            if (user?.email) {
                try {
                    await api.get(`/ping?email=${user.email}`);
                    const res = await api.get('/stats/online');
                    setOnlineUsers(res.data.online);
                } catch (e) {
                    console.error("Ошибка онлайна");
                }
            }
        };

        trackOnline();
        const interval = setInterval(trackOnline, 30000);
        return () => clearInterval(interval);
    }, [user]);

    const getRoleIcon = (role) => {
        switch (role) {
            case 'super_admin': return <UserCog size={20} color="#4f46e5" />; 
            case 'admin': return <Shield size={20} color="#2563eb" />; 
            case 'moderator': return <UserCheck size={20} color="#16a34a" />; 
            case 'ministry': return <Building size={20} color="#dc2626" />; 
            case 'chief_doctor': return <User size={20} color="#ea580c" />; 
            default: return <User size={20} color="#64748b" />; 
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isActive = (path) => location.pathname === path ? 'active' : '';

    const roleTranslations = {
        super_admin: 'Супер Администратор',
        admin: 'Администратор',
        moderator: 'Модератор',
        ministry: 'Представитель Минздрава',
        chief_doctor: 'Главный врач',
        doctor: 'Врач',
        pharmacy: 'Аптека',
        lab: 'Лаборатория'
    };

    const hasAccess = (route) => {
        const role = user?.role;
        if (!role) return false;

        if (role === 'super_admin' || role === 'admin') return true;

        if (role === 'ministry') {
            return route !== '/settings';
        }

        if (role === 'moderator') {
            const allowed = ['/', '/pharmacies', '/labs', '/prescriptions', '/drugs', '/medical-books', '/analytics', '/alerts', '/logs'];
            return allowed.includes(route);
        }

        if (role === 'chief_doctor') {
            const allowed = ['/hospitals', '/doctors', '/patients', '/logs', '/registration'];
            return allowed.includes(route);
        }

        return false;
    };

    const iconBtnStyle = {
        background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer',
        padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', transition: 'all 0.2s'
    };

    return (
        <div className="page-wrapper">

            <header className="main-header">
                <div className="header-inner">
                    <div className="header-left">
                        <div className="logo-bg">
                            <Shield className="text-white" size={24} />
                        </div>
                        <div>
                            <h1 className="brand-title">eDukhtur App</h1>
                            <p className="brand-subtitle">Добро пожаловать, {user?.full_name ? user.full_name.split(' ')[0] : roleTranslations[user?.role]}!</p>
                        </div>
                    </div>
                    
                    <div className="header-right" style={{ display: 'flex', alignItems: 'center' }}>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '4px', paddingRight: '26px', borderRight: '1px solid #e2e8f0' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', boxShadow: '0 0 8px #22c55e', animation: 'pulse 2s infinite' }}></div>
                            <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#64748b' }}>
                                Онлайн: <strong style={{ color: '#0f172a' }}>{onlineUsers}</strong>
                            </span>
                            <style>{`@keyframes pulse { 0% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.2); } 100% { opacity: 1; transform: scale(1); } }`}</style>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', marginRight: '10px', paddingRight: '20px', borderRight: '1px solid #e2e8f0' }}>
                            {hasAccess('/registration') && <button onClick={() => navigate('/registration')} title="Регистрация пользователей" style={iconBtnStyle} onMouseOver={(e) => e.currentTarget.style.color = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}><UserPlus size={20} /></button>}
                            {hasAccess('/analytics') && <button onClick={() => navigate('/analytics')} title="Аналитика" style={iconBtnStyle} onMouseOver={(e) => e.currentTarget.style.color = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}><BarChart2 size={20} /></button>}
                            {hasAccess('/alerts') && <button onClick={() => navigate('/alerts')} title="Алерты" style={iconBtnStyle} onMouseOver={(e) => e.currentTarget.style.color = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}><Bell size={20} /></button>}
                            {hasAccess('/logs') && <button onClick={() => navigate('/logs')} title="Логи активности" style={iconBtnStyle} onMouseOver={(e) => e.currentTarget.style.color = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}><Activity size={20} /></button>}
                            {hasAccess('/settings') && <button onClick={() => navigate('/settings')} title="Настройки" style={iconBtnStyle} onMouseOver={(e) => e.currentTarget.style.color = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}><Settings size={20} /></button>}
                        </div>

                        <div className="user-profile" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '50%', display: 'flex' }}>
                                {getRoleIcon(user?.role)}
                            </div>
                            
                            <div>
                                <p className="user-email" style={{fontWeight: '600', color: '#0f172a', fontSize: '0.95rem', margin: 0}}>
                                    {user?.full_name || user?.email}
                                </p>
                                <div className="user-badge" style={{ marginTop: '2px' }}>
                                    <Shield size={12} />
                                    <span>{roleTranslations[user?.role] || 'Пользователь'}</span>
                                </div>
                            </div>
                        </div>

                        <button className="logout-button" onClick={handleLogout} style={{ marginLeft: '16px' }}>
                            <LogOut size={16} />
                            Выход
                        </button>
                    </div>
                </div>
            </header>

            <div className="nav-container">
                <div className="nav-inner">
                    <nav className="nav-scroll">
                        {hasAccess('/') && (
                            <button className={`nav-link ${isActive('/')}`} onClick={() => navigate('/')}>
                                <LayoutDashboard size={16} /> Дашборд
                            </button>
                        )}
                        
                        {hasAccess('/hospitals') && (
                            <button className={`nav-link ${isActive('/hospitals')}`} onClick={() => navigate('/hospitals')}>
                                <Building size={16} /> Больницы
                            </button>
                        )}

                        {hasAccess('/doctors') && (
                            <button className={`nav-link ${isActive('/doctors')}`} onClick={() => navigate('/doctors')}>
                                <Users size={16} /> Врачи
                            </button>
                        )}

                        {hasAccess('/patients') && (
                            <button className={`nav-link ${isActive('/patients')}`} onClick={() => navigate('/patients')}>
                                <User size={16} /> Пациенты
                            </button>
                        )}

                        {hasAccess('/pharmacies') && (
                            <button className={`nav-link ${isActive('/pharmacies')}`} onClick={() => navigate('/pharmacies')}>
                                <Hexagon size={16} /> Аптеки
                            </button>
                        )}

                        {hasAccess('/labs') && (
                            <button className={`nav-link ${isActive('/labs')}`} onClick={() => navigate('/labs')}>
                                <FlaskConical size={16} /> Лаборатории
                            </button>
                        )}

                        {hasAccess('/prescriptions') && (
                            <button className={`nav-link ${isActive('/prescriptions')}`} onClick={() => navigate('/prescriptions')}>
                                <FileText size={16} /> Рецепты
                            </button>
                        )}
                        
                        {hasAccess('/drugs') && (
                            <button className={`nav-link ${isActive('/drugs')}`} onClick={() => navigate('/drugs')}>
                                <Pill size={16} /> Все о лекарствах
                            </button>
                        )}
                        
                        {hasAccess('/medical-books') && (
                            <button className={`nav-link ${isActive('/medical-books')}`} onClick={() => navigate('/medical-books')}>
                                <BookText size={16} /> Медсправочники
                            </button>
                        )}
                    </nav>
                </div>
            </div>

            <main className="main-content">
                <div className="content-wrapper">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;